/**
 * ih-order-protection-fix.js
 *
 * The OP line item is hidden from cart drawer and cart page via Liquid
 * ({% continue %} in the cart item loops). The OP toggle is the sole
 * control for adding/removing Order Protection.
 *
 * When the toggle is switched OFF, remove the OP line item from the cart
 * ourselves (the OP widget's own removeInsurance often fails with a 422).
 * Then dispatch 'cart:refresh' so the theme re-renders.
 *
 * When the toggle is switched ON, we just refresh after a delay so the
 * OP price is reflected in the cart totals.
 *
 * Uses a MutationObserver to detect when the OP widget injects its toggle
 * (it loads async from a CDN), then attaches a change listener. The
 * observer stays active because the OP widget re-initializes (destroys +
 * re-injects its toggle) on every cart innerHTML re-render.
 *
 * Observer scope (IMPORTANT — this is why the integration was removed once):
 * The observer is scoped to the cart surfaces (#CartDrawer and the .cart
 * page wrapper), NOT document.body. An earlier version observed
 * document.body with subtree:true for the whole page lifetime, which
 * reacted to every DOM mutation site-wide and interfered with the cart
 * drawer. #CartDrawer (the <cart-drawer> custom element) and the .cart
 * wrapper are NOT destroyed on innerHTML re-render — only their inner
 * content is replaced — so a subtree observer on those stable ancestors
 * still catches the OP widget's async injection and re-injection while
 * ignoring unrelated page mutations.
 *
 * Key design decisions:
 * - A removal lock (_removing) prevents concurrent /cart/change.js calls
 *   when the change event fires multiple times in quick succession or
 *   when the OP widget re-initializes the toggle programmatically.
 * - After a failed removal attempt, we retry once after a brief delay
 *   to handle transient 400/409 errors from Shopify's AJAX cart API.
 * - The 'cart:refresh' event is consumed by CartDrawer.onCartRefresh()
 *   (cart.js) which re-fetches and replaces the drawer HTML. MainCart
 *   does NOT listen for 'cart:refresh', so we handle cart page refresh
 *   manually via section rendering.
 */

(function () {
  // Singleton guard — prevents double-initialization if the script tag
  // is present in both cart-drawer.liquid and main-cart.liquid (it is).
  // This flag lives on window, so it survives across both script loads
  // but NOT across full page navigations (which is correct).
  if (window.__ihOPFixAttached) return;
  window.__ihOPFixAttached = true;

  // --- State ---

  // Lock to prevent concurrent removal attempts. Reset after each
  // complete removal cycle (success or final failure).
  var _removing = false;

  // Tracks the last toggle element we attached a listener to, so we
  // can detect when the OP widget has re-injected a fresh toggle after
  // a cart re-render (the old element is orphaned by innerHTML).
  var _lastToggleEl = null;

  // --- Helpers ---

  /** Check if a cart line item is the Order Protection product. */
  function isOPItem(item) {
    // Check vendor first (most reliable — set by OP app on the product).
    if (item.vendor && item.vendor.toLowerCase() === 'order protection') {
      return true;
    }
    // Fallback: check handle (some stores rename the product title).
    if (item.handle && item.handle.indexOf('order-protection') !== -1) {
      return true;
    }
    // Fallback: check product_title (least reliable — merchant can edit).
    if (item.product_title && item.product_title.toLowerCase().indexOf('order protection') !== -1) {
      return true;
    }
    // Fallback: check properties for OP-specific keys. Some OP versions
    // tag items with a _op or _order_protection property.
    if (item.properties) {
      var props = item.properties;
      if (props._op || props._order_protection || props['_Order Protection']) {
        return true;
      }
    }
    return false;
  }

  /**
   * Fetch the cart, find the Order Protection line item, and remove it.
   * Returns true if the OP item is confirmed gone (we removed it, or OP
   * already did). Returns false only if the item is still present after
   * our attempt.
   */
  async function removeOPFromCart() {
    try {
      var res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) {
        console.warn('[ih-op-fix] GET /cart.js failed:', res.status);
        return false;
      }
      var cart = await res.json();

      var opItem = null;
      for (var i = 0; i < cart.items.length; i++) {
        if (isOPItem(cart.items[i])) {
          opItem = cart.items[i];
          break;
        }
      }

      // Already gone — OP removed it during the wait, or it was never there.
      if (!opItem) return true;

      var updateRes = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: opItem.key, quantity: 0 })
      });

      if (!updateRes.ok) {
        // A 400/404 can mean OP removed the item between our GET and POST.
        // Re-verify with a fresh GET before treating as a real failure.
        var verifyRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!verifyRes.ok) {
          console.warn('[ih-op-fix] Verify GET /cart.js failed:', verifyRes.status);
          return false;
        }
        var verifyCart = await verifyRes.json();
        var stillPresent = verifyCart.items.some(function (item) {
          return isOPItem(item);
        });
        if (!stillPresent) return true; // OP beat us to it — that's fine
        console.warn('[ih-op-fix] Failed to remove OP item:', updateRes.status);
        return false;
      }

      return true;
    } catch (err) {
      console.warn('[ih-op-fix] Error removing OP item:', err);
      return false;
    }
  }

  /**
   * Dispatch 'cart:refresh' for the cart drawer (CartDrawer.onCartRefresh
   * listens for this in cart.js) and manually re-render the cart page if
   * we're on it (MainCart does NOT listen for 'cart:refresh').
   */
  function refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // Cart page manual refresh — only runs on /cart
    var mainCartEl = document.querySelector('[id^="MainCart-"]');
    if (mainCartEl) {
      // FoxTheme.routes is set by js-variables.liquid in <head>, which
      // loads before any deferred scripts. Safe to access here.
      var cartUrl = (window.FoxTheme && FoxTheme.routes && FoxTheme.routes.cart_url) || '/cart';
      var sectionId = mainCartEl.id.replace('MainCart-', '');
      fetch(cartUrl + '?section_id=' + sectionId)
        .then(function (res) {
          if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var updated = parsed.getElementById(mainCartEl.id);
          if (updated) {
            mainCartEl.innerHTML = updated.innerHTML;
            var cartWrapper = mainCartEl.closest('.cart');
            if (cartWrapper) {
              var hasItems = mainCartEl.querySelector('.cart-item');
              if (hasItems) {
                cartWrapper.classList.remove('is-empty');
              } else {
                cartWrapper.classList.add('is-empty');
              }
            }
          }
        })
        .catch(function (err) {
          console.warn('[ih-op-fix] Error refreshing cart page:', err);
        });
    }
  }

  /**
   * Handle toggle OFF: remove OP item and refresh cart.
   * Has retry logic — on first failure, waits briefly and retries once.
   * Uses _removing lock to prevent concurrent removals.
   */
  async function handleToggleOff() {
    if (_removing) return; // Another removal is already in progress
    _removing = true;

    try {
      // Wait briefly for OP's own removal attempt to complete (or fail).
      await new Promise(function (r) { setTimeout(r, 300); });

      var removed = await removeOPFromCart();

      if (!removed) {
        // Retry once after a short delay — handles transient errors or
        // race with OP's own removal that hasn't completed yet.
        await new Promise(function (r) { setTimeout(r, 500); });
        removed = await removeOPFromCart();
      }

      if (removed) {
        refreshCart();
      } else {
        console.warn('[ih-op-fix] Could not remove OP item after retry.');
        // Refresh anyway so user sees current cart state
        refreshCart();
      }
    } finally {
      _removing = false;
    }
  }

  /**
   * Attach a change listener to the OP toggle checkbox.
   *
   * Guard: uses a reference equality check (_lastToggleEl) instead of a
   * property on the DOM element. DOM element properties are lost when
   * innerHTML destroys the element, and the OP widget may clone or
   * recreate the element. A reference check on a module-scoped variable
   * is more robust.
   */
  function attachToggleListener(toggle) {
    // If this is the same DOM element we already attached to, skip.
    // This handles the case where MutationObserver fires multiple
    // mutations for the same toggle insertion.
    if (toggle === _lastToggleEl) return;
    _lastToggleEl = toggle;

    toggle.addEventListener('change', function () {
      var isOn = toggle.checked;

      if (!isOn) {
        // Toggle switched OFF — remove OP line item from cart.
        handleToggleOff();
      } else {
        // Toggle switched ON — OP app adds the item itself; just refresh
        // after a delay so the OP price is reflected in cart totals.
        setTimeout(refreshCart, 1500);
      }
    });
  }

  // --- Initialization ---

  // Check if toggle already exists in the DOM (OP widget loaded before us).
  var existing = document.getElementById('op-toggle');
  if (existing) attachToggleListener(existing);

  // Scan a batch of mutations for an injected #op-toggle and attach to it.
  var observerCallback = function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var addedNodes = mutations[i].addedNodes;
      for (var j = 0; j < addedNodes.length; j++) {
        var node = addedNodes[j];
        // Skip text/comment nodes
        if (node.nodeType !== 1) continue;

        var toggle;
        if (node.id === 'op-toggle') {
          toggle = node;
        } else if (node.querySelector) {
          toggle = node.querySelector('#op-toggle');
        }

        if (toggle) {
          attachToggleListener(toggle);
          // Return early — there's only one #op-toggle per page.
          return;
        }
      }
    }
  };

  var observer = new MutationObserver(observerCallback);

  // Observe the cart surfaces ONLY — never document.body.
  //
  // These are the stable ancestors that survive innerHTML re-renders:
  //   - #CartDrawer    : the <cart-drawer> custom element (rendered in the
  //                      layout on every page; only its inner
  //                      #CartDrawerBody-* is replaced on update)
  //   - .cart wrapper  : the cart page wrapper (only its inner #MainCart-*
  //                      is replaced on update)
  // subtree:true on these still catches the OP widget injecting/re-injecting
  // #op-toggle inside them, but ignores unrelated, page-wide mutations that
  // previously caused cart-drawer interference.
  function observeCartSurfaces() {
    var observed = 0;

    var drawer = document.getElementById('CartDrawer');
    if (drawer) {
      observer.observe(drawer, { childList: true, subtree: true });
      observed++;
    }

    var mainCart = document.querySelector('[id^="MainCart-"]');
    if (mainCart) {
      var cartWrapper = mainCart.closest('.cart') || mainCart.parentElement || mainCart;
      observer.observe(cartWrapper, { childList: true, subtree: true });
      observed++;
    }

    return observed;
  }

  observeCartSurfaces();
})();
